import {type ComponentType, lazy, type LazyExoticComponent} from 'react';

type AsyncLoader<T> = () => Promise<T>;

/** Memoizes one module load and allows a later call to retry after failure. */
export function createRetryableModuleLoader<T>(
  loadModule: AsyncLoader<T>
): AsyncLoader<T> {
  let modulePromise: Promise<T> | undefined;

  return () => {
    modulePromise ??= loadModule().catch(error => {
      modulePromise = undefined;
      throw error;
    });
    return modulePromise;
  };
}

/**
 * Creates a lazy component and preload function that share one module load.
 * Pass the dynamic import at the call site so the bundler can discover its
 * literal path and keep the module in a separate chunk.
 */
export function createPreloadableLazyComponent<TModule, TProps>(
  loadModule: AsyncLoader<TModule>,
  selectComponent: (module: TModule) => ComponentType<TProps>
): {
  Component: LazyExoticComponent<ComponentType<TProps>>;
  preload: AsyncLoader<TModule>;
} {
  const preload = createRetryableModuleLoader(loadModule);
  const Component = lazy(async () => ({
    default: selectComponent(await preload()),
  }));

  return {Component, preload};
}
